import { Box, Card, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ETheme } from '../../../shared/enums/user/ETheme';
import { formatDate } from '../../../shared/functions/formatDate';
import { formatPrice } from '../../../shared/functions/formatPrice';
import { getDashboardIcon } from '../../../shared/functions/getDashboardIcon';
import { handleError } from '../../../shared/functions/handleError';
import { IPieChartData } from '../../../shared/interfaces/shared/IChart';
import { IDashboardData, IUser } from '../../../shared/interfaces/user/IUser';
import { darkPalette, lightPalette } from '../../../shared/mui/palettes';
import { RootState } from '../../../store/store';
import DashboardChart from './components/DashboardChart';
import { AdminDashboardService } from './service';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { theme } = useSelector(
    (state: RootState) => state?.auth?.userData,
  ) as IUser;
  const company = useSelector((state: RootState) => state?.companySettings);
  const [data, setData] = useState<IDashboardData | null>(null);
  const [expensePieChart, setExpensePieChart] = useState<IPieChartData | null>(
    null,
  );
  const [projectPieChart, setProjectPieChart] = useState<IPieChartData | null>(
    null,
  );
  const [projectByUserPieChart, setProjectByUserPieChart] =
    useState<IPieChartData | null>(null);
  useEffect(() => {
    AdminDashboardService.getAll()
      .then((response) => {
        const {
          charts: { projectChart, expensePieChart, groupedProjectsByUser },
          cards,
        } = response;
        setData(cards);
        expensePieChart &&
          setExpensePieChart({
            ...expensePieChart,
            labels: expensePieChart?.labels?.map((label) =>
              t(`expenses.type.${label}`),
            ),
          });
        groupedProjectsByUser &&
          setProjectByUserPieChart(groupedProjectsByUser);
        projectChart &&
          setProjectPieChart({
            ...response?.charts?.projectChart,
            labels: response?.charts?.projectChart?.labels?.map((label) =>
              t(`statuses.${label}`),
            ),
          });
      })
      .catch((err) => handleError(err));
  }, [theme, t]);
  return (
    <Box>
      <Grid container spacing={2}>
        {Object.keys(data || {})?.map((dashboardItem) => {
          return (
            <Grid
              size={{
                xs: 12,
                md: 6,
                lg: 4,
              }}
              key={dashboardItem}
            >
              <Card sx={{ pb: '10px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      padding: '10px',
                      backgroundColor: `${company?.primaryColor}0D`,
                      borderRadius: '10px',
                      color: 'var(--primaryColor)',
                    }}
                  >
                    {getDashboardIcon(dashboardItem as keyof IDashboardData)}
                  </Box>
                  <Typography sx={{ marginLeft: '10px', fontWeight: 500 }}>
                    {t(`dashboard.${dashboardItem}`)}
                  </Typography>
                </Box>
                <Typography
                  variant={'h5'}
                  fontWeight={'600'}
                  sx={{ marginTop: '10px', marginBottom: '10px' }}
                >
                  {dashboardItem === 'currentBalance'
                    ? formatPrice(
                        data?.[dashboardItem as keyof IDashboardData] as number,
                      )
                    : (data?.[dashboardItem as keyof IDashboardData] as number)}
                </Typography>
                <Box
                  sx={{
                    paddingTop: '10px',
                    borderTop: `1px solid ${theme === ETheme.LIGHT ? lightPalette.primary.light : darkPalette.background.light}`,
                  }}
                >
                  <Typography
                    variant={'subtitle2'}
                    color={lightPalette.text.gray}
                    sx={{
                      fontWeight: '300',
                    }}
                  >
                    {t('general.updated')}:{' '}
                    {formatDate(new Date()?.toISOString(), 'MMMM D, YYYY')}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
        {expensePieChart && (
          <DashboardChart
            chart={expensePieChart}
            title={t('dashboard.expensesStatistics')}
          />
        )}
        {projectPieChart && (
          <DashboardChart
            chart={projectPieChart}
            title={t('dashboard.projectStatistics')}
          />
        )}
        {projectByUserPieChart && (
          <DashboardChart
            chart={projectByUserPieChart}
            title={t('dashboard.projectByUserStatistics')}
          />
        )}
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
